import React from 'react';
import { observer } from 'mobx-react';
import { routerStore } from 'core/routing';
import { useClientStore, useFrameStore } from 'apps/hooks';

import { Tooltip } from 'components';
import {
  FrameTabStyled,
  TypeStyled,
  ProjectLinkStyled,
  SpanWithEllipsis,
  SpanLinkWithEllipsis,
} from './frameTab.styled';

const workspaceRoutes = [
  'home',
  'workspace',
  'workspace.spaceFiles',
  'workspace.spaceProjects',
  /workspace\.spaceSettings\.\w/,
  'workspace.rfqs',
  'workspace.quotes',
  'workspace.orders',
  'workspace.intermediator.rfqs',
  'workspace.intermediator.quotes',
  'workspace.intermediator.orders',
  'workspace.manufacturer.rfqs',
  'workspace.manufacturer.orders',
  'make-part',
  'share-file',
];

const FrameTab = observer(() => {
  const clientStore = useClientStore();
  const frameStore = useFrameStore();
  const { activeSpace } = clientStore;

  const routeName = routerStore.route.name;
  const routeParams = routerStore.route.params;
  if (clientStore.atCommunityRoute) return null;

  let type;
  let text = '';
  let link = null;
  let tooltip = '';

  if (
    workspaceRoutes.some(route => {
      if (typeof route === 'string') return route === routeName;
      return route.test(routeName);
    })
  ) {
    type = 'Workspace: ';
    if (activeSpace) {
      if (activeSpace.spaceType === 'PROFILE') text = activeSpace.path;
      else text = activeSpace.content.title;
    }
  } else if (
    routeName.includes('project.file') &&
    !routeName.includes('project.files')
  ) {
    type = 'File: ';
    const { /* project, */ file } = frameStore;

    // This happens when this is a 404, so we show Project: projectname(link)
    if (!file) type = 'Project: ';

    if (file?.filename) text = ` / ${file?.filename}`;

    const spaceType = routeName.split('.')[0]; // profile or initiative
    const projectRoute = {
      name: `${spaceType}.project`,
      params: { [spaceType]: routeParams[spaceType], slug: routeParams.slug },
    };

    const linkText = routeParams.slug;
    tooltip = `${linkText}${text}`;

    link = <ProjectLinkStyled {...projectRoute}>{linkText}</ProjectLinkStyled>;
  } else if (routeName.includes('project') && !routeName.includes('projects')) {
    type = 'Project: ';
    const { project } = frameStore;
    text = project?.name;
  } else if (
    (routeName === 'profile' || routeName.startsWith('profile.')) &&
    !routeName.endsWith('.file')
  ) {
    type = 'Profile: ';
    text = routeParams.profile;
    // order matters, this is checked after any initiative.project has been handled
  } else if (routeName.includes('initiative') && !routeName.includes('file')) {
    type = 'Team: ';
    text = routeParams.initiative;
  } else if (routeName.includes('new-quote')) {
    type = 'New Quote';
  } else if (routeName.includes('order')) {
    type = 'Order: ';
    text = routeParams.orderId;
  } else if (routeName.includes('quote')) {
    type = 'Quote: ';
    text = routeParams.quoteId;
  } else {
    text = frameStore.file?.filename;
    if (routeName.includes('rfq')) type = 'RFQ: ';
    else if (routeName.includes('shared')) type = 'File: ';
    else if (routeName.includes('make-part.file')) type = 'File: ';
    else return null;
  }

  if (type === '' && text === '') return null;

  if (!tooltip) tooltip = text;

  return (
    <>
      {tooltip && (
        <Tooltip delayShow={500} id='frameTabTooltip' place='bottom' />
      )}
      <FrameTabStyled active data-for='frameTabTooltip' data-tip={tooltip}>
        <TypeStyled>{type}</TypeStyled>
        <SpanLinkWithEllipsis>{link}</SpanLinkWithEllipsis>
        <SpanWithEllipsis>{text}</SpanWithEllipsis>
      </FrameTabStyled>
    </>
  );
});

export default FrameTab;
